import React, { useEffect, useState } from "react";
import Input from "./form/Input";
import useTranslationHook from "../hooks/useTranslationHook";
import { useDispatch, useSelector } from "react-redux";
import { guessDateTime } from "../../utils/dateUtils";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
import { PurchaseModalValidationSchema } from "../../utils/validations";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { handleSellerConfirmModal } from "../../redux/slice/modalSlice";
import {
    fetchPropertyDetail,
    saveSellerPurchaseAgreement,
} from "../../redux/action/sellerAction";
import { toast } from "react-toastify";

function SellerConfirmPurchase() {
    const { t } = useTranslationHook();
    const show = useSelector((state) => state.modal.sellerConfirmPurchaseModal);
    let lang = useSelector((state) => state.translation.lang);
    const dispatch = useDispatch();
    const [isSubmiting, setIsSubmiting] = useState(false);

    const propertyData = useSelector(
        (state) => state.seller.property.propertyData
    );

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(PurchaseModalValidationSchema),
    });

    const sanitizedDescription = () => ({
        __html: DOMPurify.sanitize(
            lang === "en"
                ? propertyData?.description
                : propertyData?.description_ar || ""
        ),
    });

    const handleClose = () => {
        dispatch(handleSellerConfirmModal(false));
    };

    useEffect(() => {
        document.body.classList.toggle("modal-open", show);
        !show && reset();
        return () => {
            document.body.classList.remove("modal-open");
        };
    }, [show]);

    const onSubmit = (data) => {
        setIsSubmiting(true);
        dispatch(
            saveSellerPurchaseAgreement({
                property: propertyData?.id,
                seller_accepted_by: data?.agreement ? 1 : 0,
                seller_signature: data?.signature,
                seller_term_condition: data?.agreement ? 1 : 0,
            })
        )
            .then((res) => {
                if (!res.error) {
                    toast.success(
                        "You have successfully accepted the Purchase Agreement."
                    );
                    dispatch(fetchPropertyDetail(propertyData?.id));
                    handleClose();
                }
            })
            .catch((err) => errToast(err))
            .finally(() => {
                setIsSubmiting(false);
            });
    };

    if (!show) {
        return <></>;
    }

    return createPortal(
        <div
            className={`modal fade ${show ? "show" : ""} purchase-modal`}
            tabIndex="-1"
        >
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header mb-0">
                        <h5 className="modal-title">Purchase Agreement</h5>

                        <button
                            type="button"
                            className="btn-close"
                            data-bs-dismiss="modal"
                            onClick={handleClose}
                            aria-label="Close"
                        ></button>
                    </div>
                    <form
                        onSubmit={handleSubmit(onSubmit)}
                        autoComplete="off"
                        autoCapitalize="off"
                    >
                        <div className="modal-body">
                            <div className="mb-4">
                                <p className="mb0 sky-text">
                                    Please review the purchase terms and sign to
                                    confirm your purchase.
                                </p>
                            </div>

                            <div className="sky-bg mb-4">
                                <h6>{t("Auction details")}</h6>
                                <table>
                                    <tr>
                                        <td width={"35%"}>Auction ID</td>
                                        <td width={"65%"}>
                                            {`${propertyData?.auction_unique_id}`}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td width={"35%"}>Auction End Date</td>
                                        <td width={"65%"}>
                                            {` ${guessDateTime(propertyData?.bidding_end, "DD-MM-YYYY", 0, lang)} `}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Item Description</td>
                                        <td>
                                            <div
                                                dangerouslySetInnerHTML={sanitizedDescription()}
                                            />
                                        </td>
                                    </tr>
                                </table>
                            </div>

                            <div className="sky-bg mb-4">
                                <h6>Terms and Conditions</h6>
                                <div className="scroll">
                                    <p>
                                        By signing this Purchase Agreement, you
                                        acknowledge and agree to:
                                    </p>
                                    <ul>
                                        <li>
                                            Payment of the total amount shown
                                            above within 5 business days
                                        </li>
                                        <li>
                                            Collection or shipping arrangements
                                            to be made within 14 days
                                        </li>
                                        <li>
                                            All sales are final and
                                            non-refundable except as required by
                                            law
                                        </li>
                                    </ul>
                                </div>
                            </div>

                            <div className="mb-3">
                                <Input
                                    type="text"
                                    id="signature"
                                    className="form-control"
                                    placeholder={t("Type your full name")}
                                    label={t("Digital Signature") + t(" *")}
                                    labelClassname="pb5"
                                    register={register}
                                    errors={errors}
                                    {...register("signature")}
                                />
                            </div>
                            <p className="sky-text">
                                By typing your name, you agree that this
                                constitutes a legal signature.
                            </p>
                            <div className="mb-3">
                                <input
                                    id="agreementS"
                                    type="checkbox"
                                    className="css-checkbox"
                                    {...register("agreement")}
                                />
                                <label
                                    htmlFor="agreementS"
                                    className="css-label"
                                >
                                    I have read and agree to the terms and
                                    conditions of this Purchase Agreement
                                </label>
                                {errors?.["agreement"]?.message ? (
                                    <span className="text-danger">
                                        {t(errors["agreement"].message)}
                                    </span>
                                ) : (
                                    <></>
                                )}
                            </div>
                        </div>
                        <div className="d-flex">
                            <button
                                className="btn btn-sky btn-md width50"
                                type={"button"}
                                onClick={handleClose}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary btn-md width50"
                                type={"submit"}
                                disabled={isSubmiting}
                            >
                                {isSubmiting
                                    ? t("Submiting")
                                    : t("Sign & Submit")}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>,
        document.getElementById("root")
    );
}

export default SellerConfirmPurchase;
